import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect } from 'react';
import { GQLInteractionType } from 'src/generated/graph-api';
import { isUploadingAttachment } from '@/common/atoms/documents';
import { useSafeInteractionParams } from '@/common/hooks/useSafeInteractionParams';
import { getBuyerAgreementStatus } from '@/views/CreateOffer/hooks/useBuyerAgreementStatus';
import { useOfferBuyer } from '@/views/Documents/hooks/useOfferBuyer';
import { useCore } from '$api-clients/hooks';
import { getReasonOfferGenerationIsDisabled } from './OfferButtons.helpers';

export const useGetOfferButtons = () => {
  const { interactionId, interactionType } = useSafeInteractionParams();
  const uploadingAttachment = useAtomValue(isUploadingAttachment);
  const shouldFetchData =
    interactionType === GQLInteractionType.offer ||
    interactionType === GQLInteractionType.lease;

  const requestParams = shouldFetchData
    ? { params: { path: { interactionId } } }
    : null;

  const { data: attachmentData } = useCore(
    '/interactions/{interactionId}/attachments',
    requestParams,
  );

  const {
    data: envelopes,
    isLoading: envelopesLoading,
    error: envelopesError,
    mutate: mutateEnvelopes,
  } = useCore('/interactions/{interactionId}/envelopes', requestParams);

  const {
    data: packages,
    isLoading: packagesLoading,
    error: packagesError,
    mutate: mutatePackages,
  } = useCore(
    '/interactions/{interactionId}/packages',
    shouldFetchData
      ? { params: { path: { interactionId }, query: { limit: 1 } } }
      : null,
  );

  const requestError = envelopesError ?? packagesError;
  const hasRequestError = !!requestError;
  useEffect(() => {
    if (!hasRequestError) {
      return;
    }
    toast(
      'error',
      'Unable to determine if user can generate offer. Try refreshing the page.',
      { showCloseButton: true },
    );
    datadogRum.addError(requestError, { context: 'OfferButtons' });
  }, [hasRequestError, requestError]);

  const refetch = useCallback(() => {
    void mutateEnvelopes();
    void mutatePackages();
  }, [mutateEnvelopes, mutatePackages]);

  const { data: buyer, offer } = useOfferBuyer(
    interactionId || '',
    interactionType !== GQLInteractionType.offer,
  );
  const referenceDate = offer?.createdAt
    ? new Date(offer.createdAt)
    : undefined;
  const buyerAgreementStatus = getBuyerAgreementStatus(buyer, referenceDate);

  const canViewOfferDocuments = packages ? packages.length > 0 : undefined;

  const disabledTooltip = getReasonOfferGenerationIsDisabled({
    attachments: attachmentData,
    isUploadingAttachment: uploadingAttachment,
    buyerAgreementStatus,
    buyerExists: !!buyer,
    envelopes,
  });

  return {
    loading: envelopesLoading || packagesLoading,
    refetch,
    canGenerateOffer: !disabledTooltip,
    disabledTooltip,
    canViewOfferDocuments,
    buyer,
    buyerAgreementStatus,
  };
};
